/**
 * Cloudflare Image Transformation Utilities
 *
 * Generates optimized image URLs using Cloudflare's cdn-cgi/image service.
 * Security: Only transforms URLs from our CDN domain.
 *
 * When using these URLs with next/image, pass unoptimized so Vercel does not
 * re-optimize (we rely on Cloudflare only; avoids double work and cost).
 */

import { getRuntimePublicConfigValue } from '@/src/lib/runtime-public-config'

export type ImageSize = 'thumbnail' | 'preview' | 'web' | 'large' | 'original'

export interface TransformOptions {
  width?: number
  height?: number
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop'
  format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png'
  quality?: number
}

// ==================== Constants ====================

// Cache development check - doesn't change at runtime
const IS_DEV = process.env.NODE_ENV === 'development'

function getCdnBase(): string {
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_CDN_URL') ||
    process.env.NEXT_PUBLIC_CDN_URL ||
    'https://cdn.dday.io'
  )
}

function getCdnHostname(cdnBase: string): string {
  try {
    return new URL(cdnBase).hostname
  } catch {
    return 'cdn.dday.io'
  }
}

/** Preset sizes optimized for common use cases */
const SIZE_PRESETS = {
  thumbnail: { width: 200, quality: 75 },
  preview: { width: 400, quality: 80 },
  web: { width: 1200, quality: 85 },
  large: { width: 2000, quality: 90 },
} as const

// ==================== Core Functions ====================

/**
 * Get an optimized image URL for a preset size.
 * Returns original URL in dev mode or for 'original' size.
 *
 * @example
 * getImageUrl(image.imageUrl, 'preview')  // Grid thumbnail
 * getImageUrl(image.imageUrl, 'web')      // Main display
 * getImageUrl(image.imageUrl, 'original') // Download (no transform)
 */
export function getImageUrl(
  imageUrl: string | null | undefined,
  size: ImageSize = 'web'
): string {
  if (!imageUrl) return ''

  // Skip transforms for original size, dev mode, or already transformed
  if (size === 'original' || IS_DEV || imageUrl.includes('/cdn-cgi/image/')) {
    return imageUrl
  }

  const cdnBase = getCdnBase()
  const path = extractPath(imageUrl, getCdnHostname(cdnBase))
  if (!path) return imageUrl

  const { width, quality } = SIZE_PRESETS[size]
  return `${cdnBase}/cdn-cgi/image/width=${width},quality=${quality},format=auto/${path}`
}

/**
 * Get image URL with custom transform options.
 * Use for responsive images, avatars, or custom aspect ratios.
 *
 * @example
 * transformImageUrl(url, { width: 100, height: 100, fit: 'cover' }) // Avatar
 * transformImageUrl(url, { width: 800, quality: 85 })              // Responsive
 */
export function transformImageUrl(
  imageUrl: string | null | undefined,
  options: TransformOptions
): string {
  if (!imageUrl) return ''
  if (IS_DEV || imageUrl.includes('/cdn-cgi/image/')) return imageUrl

  const cdnBase = getCdnBase()
  const path = extractPath(imageUrl, getCdnHostname(cdnBase))
  if (!path) return imageUrl

  const params = buildParams(options)
  return `${cdnBase}/cdn-cgi/image/${params}/${path}`
}

// ==================== Security ====================

/** Maximum number of images that can be selected at once (prevents DoS) */
export const MAX_IMAGE_SELECTION = 100

/**
 * Validate that an image URL is from a trusted source.
 * Returns the URL if valid, null otherwise.
 *
 * @example
 * const safeUrl = validateImageUrl(userProvidedUrl)
 * if (!safeUrl) throw new Error('Invalid image URL')
 */
export function validateImageUrl(
  imageUrl: string | null | undefined
): string | null {
  if (!imageUrl || typeof imageUrl !== 'string') return null

  try {
    const url = new URL(imageUrl)
    const cdnHostname = getCdnHostname(getCdnBase())
    const allowedDomains = [cdnHostname, 'images.dday.io', 'localhost']

    // Security: Only allow HTTPS (except localhost for dev)
    if (url.protocol !== 'https:' && url.hostname !== 'localhost') {
      return null
    }

    // Security: Only allow URLs from trusted domains
    const isAllowed = allowedDomains.some(
      (domain) => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
    )

    if (!isAllowed) {
      return null
    }

    return imageUrl
  } catch {
    // Invalid URL format
    return null
  }
}

/**
 * Check if an image URL is from a trusted source.
 */
export function isValidImageUrl(imageUrl: string | null | undefined): boolean {
  return validateImageUrl(imageUrl) !== null
}

// ==================== Internal Helpers ====================

/**
 * Extract path from CDN URL with security validation.
 * Only allows URLs from our CDN domain.
 */
function extractPath(imageUrl: string, cdnHostname: string): string | null {
  try {
    const url = new URL(imageUrl)

    // Security: Only transform URLs from our CDN
    if (url.hostname !== cdnHostname) {
      return null
    }

    // Return path without leading slash
    return url.pathname.slice(1)
  } catch {
    // Invalid URL - don't transform
    return null
  }
}

/** Build Cloudflare transform params string */
function buildParams(options: TransformOptions): string {
  const parts: string[] = []

  if (options.width) parts.push(`width=${options.width}`)
  if (options.height) parts.push(`height=${options.height}`)
  if (options.fit) parts.push(`fit=${options.fit}`)
  if (options.quality) parts.push(`quality=${options.quality}`)
  parts.push(`format=${options.format || 'auto'}`)

  return parts.join(',')
}
